import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';

/**
 * Requests an offline-access authorization code from Google.
 *
 * This replaces ng-gapi, which was built on gapi.auth2. Google shut that endpoint down in 2023, so
 * the old flow could not succeed regardless of the Angular version; ng-gapi is also a ViewEngine
 * package with no release since 2020, which is what made it a blocker for Angular 16.
 *
 * The contract with the backend is unchanged: it still receives the one-time authorization code and
 * exchanges it for a refresh token itself (POST /google/login).
 */
@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
    /** Same OAuth client and scopes the gapi configuration used. */
    private static readonly CLIENT_ID = '283754219711-fri0fs3bgknn0isuhgdijote6ktlbo3p.apps.googleusercontent.com';
    private static readonly SCOPES = ['https://www.googleapis.com/auth/calendar', 'https://mail.google.com/'].join(' ');
    private static readonly SCRIPT_URL = 'https://accounts.google.com/gsi/client';

    private scriptLoaded?: Promise<void>;

    requestOfflineAccessCode(): Observable<string> {
        return from(this.openConsentScreen());
    }

    private async openConsentScreen(): Promise<string> {
        await this.loadScript();

        return new Promise<string>((resolve, reject) => {
            const client = google.accounts.oauth2.initCodeClient({
                client_id: GoogleIdentityService.CLIENT_ID,
                scope: GoogleIdentityService.SCOPES,
                ux_mode: 'popup',
                callback: (response) => {
                    if (response.error || !response.code) {
                        reject(new Error(response.error ?? 'Google returned no authorization code'));
                        return;
                    }
                    resolve(response.code);
                },
            });
            client.requestCode();
        });
    }

    /** Loaded on first use rather than from index.html, so the page does not pay for it up front. */
    private loadScript(): Promise<void> {
        if (this.scriptLoaded) {
            return this.scriptLoaded;
        }

        this.scriptLoaded = new Promise<void>((resolve, reject) => {
            if (typeof google !== 'undefined' && google.accounts?.oauth2) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = GoogleIdentityService.SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => {
                // Let the next attempt retry rather than caching the failure forever.
                this.scriptLoaded = undefined;
                reject(new Error('Could not load the Google Identity Services script'));
            };
            document.head.appendChild(script);
        });

        return this.scriptLoaded;
    }
}

/** The slice of the Google Identity Services API this service uses. */
declare const google: {
    accounts: {
        oauth2: {
            initCodeClient(config: {
                client_id: string;
                scope: string;
                ux_mode: 'popup' | 'redirect';
                callback: (response: { code?: string; error?: string }) => void;
            }): { requestCode(): void };
        };
    };
};
